import React, { useState, useMemo, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import {
	Layout,
	Menu,
	Avatar,
	Badge,
	Tooltip,
	theme as antdTheme,
	Grid,
	Drawer,
	Button,
} from "antd";
import type { MenuProps } from "antd";
import {
	IdcardOutlined,
	LogoutOutlined,
	ArrowLeftOutlined,
	MenuOutlined,
	UserOutlined,
	SettingOutlined,
	FileTextOutlined,
	DollarOutlined,
	CheckCircleOutlined,
	AuditOutlined,
	DownloadOutlined,
	SafetyOutlined,
	AccountBookOutlined,
	ApartmentOutlined,
	UserAddOutlined,
	TeamOutlined,
} from "@ant-design/icons";
import { useAppContext } from "../context/AppContext";
import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";
import { useHrmsAccess } from "../context/HRMSAccessContext";
import { useReimbursementAccess } from "../context/ReimbursementAccessContext";
import { getPageTitle } from "../config/pageConfig";

import lightLogo from "../static/cdrpl.png";
import darkLogo from "../static/cdrpl.png";

const { Sider } = Layout;
const { useBreakpoint } = Grid;

interface SidebarProps {
	collapsible: boolean;
	isMobile?: boolean;
	isSmallDesktop?: boolean;
}

export const getHRMSPageTitle = (pathname: string): string => {
	if (pathname === "/hrms" || pathname === "/hrms/") {
		return getPageTitle("HRMS");
	}

	if (pathname === "/hrms/attendance") {
		return getPageTitle("HRMS Attendance");
	}

	if (pathname === "/hrms/admin/employee-logs") {
		return getPageTitle("Employee Logs");
	}

	if (pathname === "/hrms/admin/employee-config") {
		return getPageTitle("Employee Config");
	}

	if (pathname === "/hrms/admin/salary-slips") {
		return getPageTitle("Salary Slips");
	}

	if (pathname === "/hrms/admin/salary-slip-settings") {
		return getPageTitle("Salary Slip Settings");
	}
	if (pathname === '/hrms/admin/reimbursement-exports') {
		return getPageTitle('Reimbursement Exports');
	}
	if (pathname === "/hrms/admin/reporting-managers") {
		return getPageTitle("Reporting Managers");
	}
	if (pathname === "/hrms/admin/access-management") {
		return getPageTitle("HRMS Access Management");
	}
	if (pathname === "/hrms/team/attendance") {
		return getPageTitle("My Team Attendance");
	}
	if (pathname === "/hrms/reimbursements") {
		return getPageTitle("Reimbursements");
	}

	if (pathname.startsWith("/hrms/reimbursements/create")) {
		return getPageTitle("Create Reimbursement");
	}

	if (/\/hrms\/reimbursements\/\d+$/.test(pathname)) {
		return getPageTitle("Reimbursement Details");
	}

	if (pathname === "/hrms/reimbursements/pending-reporting-manager") {
		return getPageTitle("Pending RM Approval");
	}

	if (pathname === "/hrms/reimbursements/pending-review") {
		return getPageTitle("Pending Review");
	}

	if (pathname === "/hrms/reimbursements/pending-review-2") {
		return getPageTitle("Pending Review 2");
	}

	if (pathname === "/hrms/reimbursements/pending-recommendation") {
		return getPageTitle("Pending Recommendation");
	}

	if (pathname === "/hrms/reimbursements/pending-approval") {
		return getPageTitle("Pending Approval");
	}

	return getPageTitle("HRMS");
};

const HRMSNavigation = React.memo(() => {
	const screens = useBreakpoint();
	const isMobile = !screens.md;
	const isSmallDesktop = screens.md && !screens.lg;
	return <Sidebar collapsible={!isMobile} isMobile={isMobile} isSmallDesktop={isSmallDesktop} />;
});

const Sidebar = React.memo(({ collapsible, isMobile, isSmallDesktop }: SidebarProps) => {
	const location = useLocation();
	const { state: stateApp, appContextAction } = useAppContext();
	const { isNavMenuClose } = stateApp;
	const { navMenu } = appContextAction;
	const { theme } = useTheme();
	const { user, logout } = useAuth();
	const { canAdmin, canEmployeeConfigRead, canSalarySlipRead, isReportingManager } = useHrmsAccess();
	const { canReview, canReview2, canRecommend, canSanction, canApproveAsRM } = useReimbursementAccess();
	const isDarkMode = theme.algorithm?.[0] === antdTheme.darkAlgorithm;
	const logoPath = isDarkMode ? darkLogo : lightLogo;

	const [drawerVisible, setDrawerVisible] = useState(false);

	const getThemeStyles = useMemo(
		() => ({
			sidebarHeader: {
				padding: isNavMenuClose && !isMobile ? "8px" : "0 16px",
				display: "flex",
				flexDirection: isNavMenuClose && !isMobile ? ("column" as const) : ("row" as const),
				alignItems: "center",
				justifyContent: isNavMenuClose && !isMobile ? "center" : "space-between",
				overflow: "hidden",
				transition: "all 0.3s ease",
				borderBottom: "none",
				backgroundColor: "transparent",
			},
			logoContainer: {
				display: "flex",
				alignItems: "center",
				paddingBottom: isNavMenuClose && !isMobile ? "8px" : "0",
				justifyContent: isNavMenuClose && !isMobile ? "center" : "flex-start",
				marginBottom: isNavMenuClose && !isMobile ? "8px" : "0",
			},
			logo: {
				width: isNavMenuClose && !isMobile ? "50px" : isSmallDesktop ? "180px" : "200px",
				height: "auto",
				transition: "width 0.3s ease",
			},
			userEmail: {
				fontSize: "12px",
				opacity: 0.85,
				overflow: "hidden",
				textOverflow: "ellipsis",
				whiteSpace: "nowrap",
				color: isDarkMode ? "rgba(255,255,255,0.65)" : "rgba(0,0,0,0.65)",
			},
			collapsedProfile: {
				padding: "16px 0",
				display: "flex",
				justifyContent: "center",
				borderBottom: "none",
				marginBottom: "8px",
				backgroundColor: "transparent",
			},
			navigationMenu: {
				flex: 1,
				overflowY: "auto" as const,
				overflowX: "hidden" as const,
				padding: "0",
				height: "auto",
				minHeight: 0,
			},
			sidebarStyle: {
				boxShadow: "none",
				zIndex: 1000,
				height: "100vh",
				position: "fixed" as const,
				left: 0,
				display: "flex",
				flexDirection: "column" as const,
				transition: "all 0.2s ease",
				background: "transparent",
				backgroundColor: "transparent",
				overflow: "hidden",
				...(isSmallDesktop && !isNavMenuClose && {
					width: "220px !important",
				}),
			},
		}),
		[isDarkMode, isNavMenuClose, isMobile, isSmallDesktop]
	);

	const currentPath = useMemo(() => {
		const path = location.pathname;
		if (path === "/hrms" || path === "/hrms/") return "dashboard";
		if (path === "/hrms/employees") return "employees";
		if (path === "/hrms/departments") return "departments";
		if (path === "/hrms/designations") return "designations";
		if (path === "/hrms/admin/employee-logs") return "employee-logs";
		if (path === "/hrms/admin/employee-config") return "employee-config";
		if (path === "/hrms/admin/salary-slips") return "salary-slips";
		if (path === "/hrms/admin/salary-slip-settings") return "salary-slip-settings";
		if (path === "/hrms/admin/reimbursement-exports") return "reimbursement-exports";
		if (path === "/hrms/reimbursements/pending-reporting-manager") return "reimbursement-pending-reporting-manager";
		if (path === "/hrms/reimbursements/pending-review") return "reimbursement-pending-review";
		if (path === "/hrms/reimbursements/pending-review-2") return "reimbursement-pending-review-2";
		if (path === "/hrms/reimbursements/pending-recommendation") return "reimbursement-pending-recommendation";
		if (path === "/hrms/reimbursements/pending-approval") return "reimbursement-pending-approval";
		if (path === "/hrms/admin/reporting-managers") return "reporting-managers";
		if (path === "/hrms/admin/access-management") return "access-management";
		if (path === "/hrms/team/attendance") return "team-attendance";
		if (path === "/hrms/reimbursements" || path.startsWith("/hrms/reimbursements/create") || /\/hrms\/reimbursements\/\d+/.test(path)) return "reimbursements";
		const segments = path.split("/").filter(Boolean);
		return segments.length > 0 ? segments[segments.length - 1] : "";
	}, [location.pathname]);

	const menuItems: MenuProps["items"] = useMemo(() => {
		const items: MenuProps["items"] = [
			{
				key: "attendance",
				icon: <IdcardOutlined />,
				label: <Link to="/hrms/attendance">Attendance</Link>,
			},
			...(isReportingManager
				? [
					{
						key: "team-attendance",
						icon: <TeamOutlined />,
						label: <Link to="/hrms/team/attendance">My Team Attendance</Link>,
					},
				]
				: []),
			{
				key: "reimbursements",
				icon: <DollarOutlined />,
				label: <Link to="/hrms/reimbursements">Reimbursements</Link>,
			},
		];

		if (canApproveAsRM) {
			items.push({
				key: "reimbursement-pending-reporting-manager",
				icon: <CheckCircleOutlined />,
				label: <Link to="/hrms/reimbursements/pending-reporting-manager">Pending RM Approval</Link>,
			});
		}

		if (canReview) {
			items.push({
				key: "reimbursement-pending-review",
				icon: <CheckCircleOutlined />,
				label: <Link to="/hrms/reimbursements/pending-review">Pending Review</Link>,
			});
		}

		if (canReview2) {
			items.push({
				key: "reimbursement-pending-review-2",
				icon: <CheckCircleOutlined />,
				label: <Link to="/hrms/reimbursements/pending-review-2">Pending Review 2</Link>,
			});
		}

		if (canRecommend) {
			items.push({
				key: "reimbursement-pending-recommendation",
				icon: <AuditOutlined />,
				label: <Link to="/hrms/reimbursements/pending-recommendation">Pending Recommendation</Link>,
			});
		}

		if (canSanction) {
			items.push({
				key: "reimbursement-pending-approval",
				icon: <SafetyOutlined />,
				label: <Link to="/hrms/reimbursements/pending-approval">Pending Approval</Link>,
			});
		}

		if (canEmployeeConfigRead || canAdmin || canSalarySlipRead) {
			items.push({
				key: "admin",
				icon: <SettingOutlined />,
				label: "Admin",
				children: [
					...(canEmployeeConfigRead
						? [{
						key: "employee-config",
						icon: <UserOutlined />,
						label: <Link to="/hrms/admin/employee-config">Employee Config</Link>,
					}]
						: []),
					...(canAdmin
						? [
					{
						key: "employee-logs",
						icon: <FileTextOutlined />,
						label: <Link to="/hrms/admin/employee-logs">Employee Logs</Link>,
					},
						]
						: []),
					...(canSalarySlipRead
						? [
							{
								key: "salary-slips",
								icon: <AccountBookOutlined />,
								label: <Link to="/hrms/admin/salary-slips">Salary Slips</Link>,
							},
						]
						: []),
					...(canAdmin
						? [
							{
								key: "salary-slip-settings",
								icon: <SettingOutlined />,
								label: <Link to="/hrms/admin/salary-slip-settings">Salary Slip Settings</Link>,
							},
							{
								key: "reimbursement-exports",
								icon: <DownloadOutlined />,
								label: <Link to="/hrms/admin/reimbursement-exports">Reimbursement Exports</Link>,
							},
						]
						: []),
					...(canAdmin
						? [
							{
								key: "reporting-managers",
								icon: <ApartmentOutlined />,
								label: <Link to="/hrms/admin/reporting-managers">Reporting Managers</Link>,
							},
							{
								key: "access-management",
								icon: <UserAddOutlined />,
								label: <Link to="/hrms/admin/access-management">Access Management</Link>,
							},
						]
						: []),
				],
			});
		}

		items.push(
			{ type: "divider" },
			{
				key: "back-to-stages",
				icon: <ArrowLeftOutlined />,
				label: <Link to="/project-selection">Back to Previous</Link>,
			},
			{
				key: "logout",
				icon: <LogoutOutlined />,
				label: "Logout",
				onClick: logout,
				danger: true,
			}
		);

		return items;
	}, [logout, canAdmin, canEmployeeConfigRead, canReview, canReview2, canRecommend, canSanction, canSalarySlipRead, canApproveAsRM, isReportingManager]);

	const toggleCollapse = useCallback(() => {
		if (isNavMenuClose) {
			navMenu.expand();
		} else {
			navMenu.collapse();
		}
	}, [isNavMenuClose, navMenu]);

	const handleMenuClick = useCallback(
		(info: any) => {
			if (info.key === "logout") {
				logout();
			}

			if (isMobile) {
				setDrawerVisible(false);
			}
		},
		[logout, isMobile]
	);

	const toggleDrawer = useCallback(() => {
		setDrawerVisible(prev => !prev);
	}, []);

	const SidebarContent = useMemo(
		() => (
			<div
				className="transparent-nav"
				style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}
			>
				<div className="sidebar-header" style={getThemeStyles.sidebarHeader}>
					<div className="logo-container" style={getThemeStyles.logoContainer}>
						<img src={logoPath} alt="CDRPL" style={getThemeStyles.logo} />
					</div>
				</div>

				{isNavMenuClose && !isMobile && (
					<div style={getThemeStyles.collapsedProfile}>
						<Tooltip title={user?.name || "User"} placement="right">
							<Badge dot status="success" offset={[-4, 28]}>
								<Avatar icon={<UserOutlined />} size={36} />
							</Badge>
						</Tooltip>
					</div>
				)}

				<div className="navigation-menu" style={getThemeStyles.navigationMenu}>
					<Menu
						mode="inline"
						selectedKeys={[currentPath]}
						defaultOpenKeys={isMobile ? ["main", "hr", "admin"] : ["admin"]}
						items={menuItems}
						onClick={handleMenuClick}
						style={{ border: "none", background: "transparent", height: "100%" }}
						theme={isDarkMode ? "dark" : "light"}
					/>
				</div>
			</div>
		),
		[
			getThemeStyles,
			logoPath,
			isNavMenuClose,
			isMobile,
			user,
			currentPath,
			menuItems,
			handleMenuClick,
			isDarkMode,
		]
	);

	if (isMobile) {
		return (
			<>
				<Button
					type="primary"
					shape="circle"
					icon={<MenuOutlined />}
					onClick={toggleDrawer}
					style={{
						position: "fixed",
						top: "16px",
						left: "16px",
						zIndex: 999,
						boxShadow: "0 3px 6px rgba(0,0,0,0.2)",
					}}
					size="middle"
				/>
				<Drawer
					placement="left"
					onClose={toggleDrawer}
					open={drawerVisible}
					width={320}
					styles={{
						body: {
							padding: 0,
							height: "100%",
							background: "transparent",
							overflow: "hidden",
							display: "flex",
							flexDirection: "column",
						},
					}}
					closable={false}
				>
					{SidebarContent}
				</Drawer>
			</>
		);
	}

	return (
		<Sider
			collapsible={collapsible}
			collapsed={isNavMenuClose}
			onCollapse={toggleCollapse}
			className="navigation-sidebar transparent-nav"
			theme={isDarkMode ? "dark" : "light"}
			width={isSmallDesktop ? 220 : 260}
			collapsedWidth={80}
			style={getThemeStyles.sidebarStyle}
			trigger={null}
		>
			{SidebarContent}
		</Sider>
	);
});

export default HRMSNavigation;
